import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trash2, Check, ChevronRight, ChevronDown, MessageCircle, Globe, BookOpen } from "lucide-react";

export default function FeedbackPanel({ onCountsChange }) {
  const [rows, setRows] = useState(null);
  const [showReviewed, setShowReviewed] = useState(false);

  const load = () =>
    api.get("/admin/feedback").then(r => setRows(r.data)).finally(() => {
      if (onCountsChange) onCountsChange();
    });
  useEffect(() => { load(); }, []);

  const markReviewed = async (id, reviewed) => {
    try { await api.put(`/feedback/${id}/reviewed`, { reviewed });
          toast.success(reviewed ? "Marked reviewed" : "Marked unreviewed"); load(); }
    catch { toast.error("Could not update"); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this feedback? This cannot be undone.")) return;
    try { await api.delete(`/feedback/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  if (rows === null) return <div className="mt-6 text-slate-500">Loading…</div>;

  const pending = rows.filter(f => !f.reviewed_at);
  const reviewed = rows.filter(f => f.reviewed_at);

  const renderRow = (f) => (
    <li key={f.feedback_id} data-testid={`admin-feedback-${f.feedback_id}`} className="py-5">
      <div className="flex items-start gap-3">
        {f.user_picture
          ? <img src={f.user_picture} alt="" className="w-9 h-9 rounded-full"/>
          : <div className="w-9 h-9 rounded-full bg-slate-200"/>}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="font-semibold text-slate-900">{f.user_name || "Anonymous"}</span>
            <span className="text-xs text-slate-400">{f.user_email}</span>
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded px-1.5 py-0.5 border ${
              f.type === "blog" ? "bg-sky-50 text-sky-800 border-sky-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}>
              {f.type === "blog" ? <BookOpen className="w-3 h-3"/> : <Globe className="w-3 h-3"/>}
              {f.type}
            </span>
            {f.type === "blog" && f.blog_slug && (
              <Link to={`/blogs/${f.blog_slug}`} className="text-sky-700 hover:text-sky-900 truncate text-sm">
                {f.blog_title || f.blog_slug}
              </Link>
            )}
            <span className="text-slate-400 ml-auto text-xs">{new Date(f.created_at).toLocaleString()}</span>
          </div>
          <p className="text-slate-800 mt-1 whitespace-pre-wrap">{f.body}</p>
          <div className="mt-2 flex items-center gap-3 text-xs uppercase tracking-widest">
            <button data-testid={`admin-feedback-review-${f.feedback_id}`}
                    onClick={() => markReviewed(f.feedback_id, !f.reviewed_at)}
                    className={`inline-flex items-center gap-1 ${f.reviewed_at ? "text-slate-500 hover:text-slate-900" : "text-emerald-700 hover:text-emerald-900"}`}>
              <Check className="w-3.5 h-3.5"/>
              {f.reviewed_at ? "Un-review" : "Mark reviewed"}
            </button>
            <button data-testid={`admin-feedback-delete-${f.feedback_id}`}
                    onClick={() => remove(f.feedback_id)}
                    className="text-red-500 hover:text-red-700 inline-flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5"/>Delete
            </button>
          </div>
        </div>
      </div>
    </li>
  );

  return (
    <div className="space-y-6">
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading flex items-center justify-between gap-2 flex-wrap">
            <span className="inline-flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-sky-700"/>
              Pending review <span className="text-slate-400 font-normal">· {pending.length}</span>
            </span>
            <span className="text-xs uppercase tracking-widest text-slate-500 font-normal">{rows.length} total</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pending.length === 0 ? (
            <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-8 text-center">
              <MessageCircle className="w-6 h-6 mx-auto text-slate-300 mb-2"/>
              All caught up — no new feedback.
            </div>
          ) : (
            <ul className="divide-y divide-slate-200" data-testid="admin-feedback-pending">
              {pending.map(renderRow)}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading flex items-center justify-between gap-2 flex-wrap">
            <button data-testid="feedback-reviewed-toggle" onClick={() => setShowReviewed(v => !v)}
                    className="flex items-center gap-2 text-slate-800 hover:text-sky-700">
              {showReviewed ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
              <span>Reviewed <span className="text-slate-400 font-normal">· {reviewed.length}</span></span>
            </button>
            {reviewed.length > 0 && !showReviewed && (
              <span className="text-xs uppercase tracking-widest text-slate-400">Click to view</span>
            )}
          </CardTitle>
        </CardHeader>
        {showReviewed && (
          <CardContent>
            {reviewed.length === 0
              ? <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-8 text-center">No reviewed feedback yet.</div>
              : <ul className="divide-y divide-slate-200" data-testid="admin-feedback-reviewed">{reviewed.map(renderRow)}</ul>}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
